import {
  HStack,
  Text,
  Badge,
  Icon,
  Dialog,
  CloseButton,
  Button,
  Spinner,
  Portal,
} from "@chakra-ui/react";
import { Tooltip } from "../ui/tooltip";
import useThemeColors from "../../hooks/useThemeColors";
import useIcons from "../../hooks/useIcons";
import DeleteAlertDialogModal from "./DeleteAlertDialogModal";
import { IconType } from "react-icons";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  handleDelete?: () => void;
  deleteMessage?: string;
  trigger?: React.ReactNode;
  icon?: IconType;
  iconColor?: string;
  title?: string;
  mode?: "edit" | "view" | "add";
  handleClose: () => void;
  hasChanges?: boolean;
  hasValidationErrors?: boolean;
  isLoading?: boolean;
  handleAction?: () => void;
  actionText?: string;
  isDisabled?: boolean;
  openDeleteDialog?: boolean;
  setOpenDeleteDialog?: (open: boolean) => void;
  hideCancelButton?: boolean;
}

function CustomModal({
  isOpen,
  handleDelete,
  deleteMessage,
  trigger,
  icon,
  iconColor,
  title,
  mode,
  children,
  handleClose,
  hasChanges,
  hasValidationErrors = false,
  isLoading = false,
  handleAction,
  actionText = "Save",
  isDisabled,
  openDeleteDialog = false,
  setOpenDeleteDialog,
  hideCancelButton = false,
}: Props) {
  const colors = useThemeColors();
  const icons = useIcons();

  return (
    <>
      <DeleteAlertDialogModal
        handleDelete={handleDelete}
        message={deleteMessage}
        openDeleteDialog={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
        isDeleting={isLoading}
      />
      <Dialog.Root
        open={isOpen}
        
        closeOnInteractOutside={false}
        motionPreset="slide-in-bottom"
        scrollBehavior="inside"
        size="xl"
        closeOnEscape
        onOpenChange={(e) => {
          if (!e.open) {
            handleClose();
          }
        }}
      >
        {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
        <Portal>
          <Dialog.Backdrop bg="rgba(0, 0, 0, 0.5)" backdropFilter="blur(2px)" />
          <Dialog.Positioner>
            <Dialog.Content
              maxH={{ base: "calc(100svh - 70px)", md: "90vh" }}
              maxW={{ base: "calc(100vw - 32px)", md: "xl" }}
              bg={colors.background.tertiary}
            >
              <Dialog.CloseTrigger asChild disabled={isLoading}>
                <CloseButton />
              </Dialog.CloseTrigger>
              <Dialog.Header
                borderBottom="1px"
                borderColor={colors.border.primary}
              >
                <Dialog.Title
                  justifyItems="space-between"
                  alignItems="center"
                  gap={3}
                  display="flex"
                >
                  {icon && <Icon as={icon} color={iconColor} />}
                  <Text>{title}</Text>
                  {mode && (
                    <Badge colorPalette="gray" variant="subtle" fontSize="xs">
                      {mode === "edit"
                        ? "Edit Mode"
                        : mode === "add"
                          ? "Add Mode"
                          : "View Mode"}
                    </Badge>
                  )}
                </Dialog.Title>
              </Dialog.Header>

              <Dialog.Body p={6}>{children}</Dialog.Body>

              {/* Sticky Footer */}
              <Dialog.Footer
                borderTop="1px"
                borderColor={colors.border.primary}
                bg={colors.background.secondary}
              >
                <HStack gap={3} w="full" justify="space-between">
                  <HStack>
                    {mode === "edit" && handleDelete && (
                      <Button
                        variant="plain"
                        colorPalette="red"
                        onClick={() => setOpenDeleteDialog?.(true)}
                      >
                        Delete
                      </Button>
                    )}
                  </HStack>

                  <HStack gap={3}>
                    {!hideCancelButton && (
                      <Button
                        variant="outline"
                        onClick={handleClose}
                        size="md"
                        display={{ base: "none", md: "block" }}
                        disabled={isLoading}
                      >
                        <Icon as={icons.utility.cancel} />
                        Cancel
                      </Button>
                    )}

                    <Tooltip
                      content={
                        hasChanges ? actionText || "Save" : "No valid changes"
                      }
                      disabled={false}
                      asChild
                    >
                      <Button
                        colorPalette={colors.schoolAccent.primary}
                        onClick={handleAction}
                        disabled={
                          !hasChanges ||
                          hasValidationErrors ||
                          isLoading ||
                          isDisabled
                        }
                      >
                        {isLoading ? (
                          <Spinner />
                        ) : mode === "edit" ? (
                          "Save Changes"
                        ) : (
                          actionText || "Save"
                        )}
                      </Button>
                    </Tooltip>
                  </HStack>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
}

export default CustomModal;
